import { useEffect, useMemo, useState } from "react";
import {
  Container,
  ErrorMessage,
  ShowIf,
  SinglePageable,
  SinglePageableResponse,
} from "../../../core/component";
import { Button, Dropdown, Form, message } from "antd";
import { confirm, notify, translate } from "../../../core";
import {
  CloseOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  SaveFilled,
  SearchOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  BadRequestException,
  EntryMode,
  PageableRequest,
  PageContextValue,
} from "../../../core/type";
import { Contact, WebInfoType } from "../type/contact";
import { ContactForm } from "./ContactForm";
import { ContactSearch } from "./ContactSearch";
import { EnvelopIcon } from "../../../component/icon";
import { ContactEntry } from "../type/contact-entry";
import { ContactPageContext } from "./ContactPageContext";
import { useContactData } from "../hook/contact-data.hook";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  ContactSendEmail,
  GetContactPage,
  GetContacts,
} from "../graphql/contact.queries.graphql";
import { ContactFindRequest } from "../type/contact.query";
import ContactHistory from "./ContactHistory";
import { useNavigate, useParams } from "react-router-dom";
import { ReportPdfViewer } from "../../../core/component/Report";
import { SendEmail } from "../../../core/component/SendEmail";
import { ContactOnlineUserList } from "./ContactOnlineUserList";

export const ContactPage = () => {
  const { contactId } = useParams<{ contactId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [entity, setEntity] = useState<Contact>();
  const [page, setPage] = useState<number>();
  const [errors, setErrors] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [entryMode, setEntryMode] = useState<EntryMode>(EntryMode.Nothing);
  const [form] = Form.useForm<ContactEntry>();
  const [find] = useLazyQuery(GetContacts);
  const [findRequest, setFindRequest] = useState<ContactFindRequest>();
  const { init, toEntry, toDto, create, update, remove } = useContactData();
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [isRefetch, setIsRefetch] = useState<boolean>(false);
  const [pdfReportOpen, setPdfReportOpen] = useState(false);
  const [data, setData] = useState<Contact | null>(null);
  const [openSendEmail, setOpenSendEmail] = useState<boolean>(false);
  const [openOnlineUserList, setOpenOnlineUserList] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  useQuery(GetContactPage, {
    variables: {
      contactId: contactId ?? "",
    },
    skip: !contactId,
    onCompleted: (dataPage) => {
      if (data?.contactId !== contactId) {
        fetch({}, dataPage.contactPage)
      }
    },
  });

  const [sendContactEmail, { loading: sendingContactByEmail }] = useMutation(
    ContactSendEmail,
    {
      onCompleted: () => {
        setOpenSendEmail(false);
        messageApi.success(translate("email", "sent"));
      },
      onError: (error) => messageApi.error(error.message),
    }
  );

  useEffect(() => {
    setIsFormChanged(false);
  }, [entity]);

  const printDocument = () => {
    window.print();
  };

  const prepareEntity = (entity?: Contact, mode?: EntryMode) => {
    if (!entity && mode === EntryMode.New) {
      form.resetFields();
      entity = init();
    }

    setEntryMode(mode ?? EntryMode.Modify);
    setEntity(entity);
    form.setFieldsValue(toEntry(entity ?? ({} as Contact)));
  };

  const save = async (entry: ContactEntry) => {
    setSending(true);
    setErrors([]);

    const dto = toDto(entry);
    const id = entity?.id;

    try {
      if (id) await update(id, dto);
      else {
        const result = await create(dto);
        if (entity) entity.id = result.id;
      }

      notify.success(translate("contact"), translate("save_success_message"));

      setIsFormChanged(false);

      await refresh();
    } catch (e) {
      if (e instanceof BadRequestException) setErrors(e.message);
    } finally {
      setSending(false);
      setIsRefetch(true);
    }
  };

  const handleRemove = async () => {
    if (!entity) return;

    const result = await confirm(
      translate("remove_confirmation_message", "contact", "?")
    );

    if (!result) return;

    await remove(entity.id);

    notify.success(
      translate("remove", "contact"),
      translate("the", "contact", "remove_success_message")
    );

    close();
  };

  const refresh = async () => {
    const { data } = await find({
      variables: {
        request: {
          take: 1,
          skip: 0,
          id: entity?.id ?? 0,
        },
      },
    });
    const [result] = data?.contactsFind.data ?? [];
    prepareEntity(result);
  };

  const onChange = async (
    request: PageableRequest,
    page: number
  ): Promise<SinglePageableResponse<Contact>> => {
    setLoading(true);

    try {
      const { data } = await find({
        variables: {
          request: {
            take: request.take ?? 1,
            skip: request.skip ?? 0,
            ...findRequest,
          },
        },
      });

      const [entity] = data?.contactsFind.data ?? [];
      navigate(`/contact/${entity?.contactId}`);
      setData(entity);

      if (!entity) {
        setEntity(undefined);
      } else prepareEntity(entity);

      if (!page) setPage(1);

      return {
        count: data?.contactsFind.count ?? 0,
      };
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setEntity(undefined);
    setEntryMode(EntryMode.Nothing);
  };

  const fetch = (findRequest: ContactFindRequest, page: number = 1) => {
    setFindRequest(findRequest);
    setPage(0);
    setTimeout(() => setPage(page), 500);
  };

  const contextValue = useMemo<PageContextValue<any, any>>(
    () => ({
      entity,
      form,
      save,
      fetch,
      refresh,
      onFormChange: () => setIsFormChanged(true),
    }),
    [entity, form, save, fetch, refresh]
  );

  return (
    <ContactPageContext.Provider value={contextValue}>
      <Container
        leftClassName="action-container"
        paddingClass="0"
        loading={loading}
        toolbarItems={{
          start: [
            <div key="search-add-group" className="button-group">
              <Button
                key={translate("search")}
                onClick={() => setSearchOpen(true)}
                icon={<SearchOutlined />}
                className="btn-primary-gradient"
              >
                {translate("search")}
              </Button>
              <Button
                key={translate("add")}
                onClick={() => prepareEntity(undefined, EntryMode.New)}
                icon={<PlusOutlined />}
                className="btn-primary-gradient"
              >
                {translate("add")}
              </Button>
            </div>,

            <div key="save-remove-clear-group" className="button-group">
              <Button
                key={translate("save")}
                loading={sending}
                onClick={() => form.submit()}
                disabled={!entity}
                type={isFormChanged ? "primary" : "default"}
                icon={<SaveFilled />}
              >
                {translate("save")}
              </Button>
              <Button
                key={translate("remove")}
                danger
                disabled={!entity?.id}
                onClick={handleRemove}
                icon={<DeleteOutlined />}
              >
                {translate("remove")}
              </Button>
              <Button
                key={translate("clear")}
                disabled={entryMode === EntryMode.Nothing}
                onClick={async () => {
                  if (entryMode === EntryMode.New) {
                    form.resetFields();
                    setIsFormChanged(false);
                  }
                  if (entryMode === EntryMode.Modify) await refresh();
                }}
                icon={<StopOutlined />}
              >
                {translate("clear")}
              </Button>
            </div>,

            <div key="actions-group" className="button-group">
              <Button
                key={translate("email")}
                onClick={() => setOpenSendEmail(true)}
                icon={<EnvelopIcon />}
              >
                {translate("email")}
              </Button>
              <Dropdown.Button
                key="historyDropdown"
                icon={<HistoryOutlined />}
                disabled={!entity?.id}
                menu={{
                  items: [
                    {
                      key: "legacy",
                      label: translate("legacy"),
                    },
                  ],
                  onClick: (e) =>
                    e.key === "legacy" && entity?.id && printDocument(),
                }}
                onClick={() => setPdfReportOpen(true)}
              >
                {translate("history")}
              </Dropdown.Button>
            </div>,

            <div key="exit-group" className="button-group">
              <Button
                key={translate("exit")}
                onClick={close}
                icon={<CloseOutlined />}
              >
                {translate("exit")}
              </Button>
            </div>,

            <Button
              icon={<UserOutlined />}
              onClick={() => setOpenOnlineUserList(true)}
            >
              {translate("online", "users")}
            </Button>
          ],
          end: [
            <SinglePageable
              key={translate("pagination")}
              onChange={onChange}
              defaultPage={page}
            />,
          ],
        }}
      >
        <ContactSearch open={searchOpen} closeMe={() => setSearchOpen(false)} />

        {entity && (
          <>
            <div className="p-3 bg-gray print-hidden">
              <ErrorMessage
                title={translate("contact_entry_fail_message")}
                message={errors}
              />

              <ShowIf condition={!!entity}>
                <ContactForm />
              </ShowIf>
            </div>
            <ContactHistory
              id={entity.id}
              isRefetch={isRefetch}
              fetched={() => setIsRefetch(false)}
            />
          </>
        )}
      </Container>

      <ReportPdfViewer
        name="sales-by-customer"
        title={translate("sales", "by", "customer")}
        open={pdfReportOpen}
        onCancel={() => setPdfReportOpen(false)}
        params={{
          customerId: entity?.id,
        }}
      />

      <SendEmail
        open={openSendEmail}
        onCancel={() => setOpenSendEmail(false)}
        onSend={input =>
          sendContactEmail({
            variables: {
              input,
            },
          })
        }
        sending={sendingContactByEmail}
        attachment={{
          enabled: true,
          prefix: "contact",
        }}
        emailInputEnabled={{
          email: entity?.webInfos.find(wi => wi.type === WebInfoType.EmailAddress)?.value,
        }}
      />

      <ContactOnlineUserList
        open={openOnlineUserList}
        onClose={() => setOpenOnlineUserList(false)}
      />
    </ContactPageContext.Provider>
  );
};
